"use client";

import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@apollo/client";
import { GET_REACTIONS } from "@/api/apollo-client/queries/Queries";
import ReactionCard, { Reaction_INFO } from "@/components/ReactionCard";
import { ArrowLeft } from "lucide-react";
import SlidingPanel from "@/components/ui/SlidingPanel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePagination } from "@/hooks/Pagination/usePagination";

type ReactionsPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  postId: string | null;
};

type PaginatedReaction = Reaction_INFO & { id: string };

const PAGE_SIZE = 30;

export default function ReactionsPanel({
  isOpen,
  onClose,
  postId,
}: ReactionsPanelProps) {
  const router = useRouter();
  const loaderRef = useRef<HTMLDivElement>(null);
  const isFirstLoadRef = useRef(true);

  const { data, loading, fetchMore, error } = useQuery(GET_REACTIONS, {
    variables: postId
      ? { input: { post_id: postId, pagination: { first: PAGE_SIZE } } }
      : undefined,
    skip: !isOpen || !postId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  // When postId changes while panel is open, force a refetch by relying on key at parent
  // Additionally, on open, ensure network fetch
  useEffect(() => {
    if (isOpen) {
      isFirstLoadRef.current = false;
    } else {
      isFirstLoadRef.current = true;
    }
  }, [isOpen, postId]);

  const initialData = useMemo(
    () =>
      (data?.getReactions || []).map((r: Reaction_INFO) => ({
        ...r,
        id: r.reaction_id,
      })),
    [data?.getReactions]
  );

  const extractNewItems = useCallback(
    (result: any) =>
      (result?.data?.getReactions || []).map((r: Reaction_INFO) => ({
        ...r,
        id: r.reaction_id,
      })),
    []
  );

  const getLastCursor = useCallback((items: PaginatedReaction[]) => {
    const lastItem = items[items.length - 1];
    if (!lastItem) return null;
    return { id: lastItem.id, timestamp: "" };
  }, []);

  const additionalVariables = useMemo(() => {
    if (!postId) return {} as any;
    return { post_id: postId } as any;
  }, [postId]);
  const getItemId = useCallback(
    (item: PaginatedReaction) => item.reaction_id,
    []
  );

  const {
    items: reactions,
    loadMore,
    loading: loadingMore,
    hasMore,
  } = usePagination<PaginatedReaction>({
    fetchMore,
    initialData,
    pageSize: PAGE_SIZE,
    extractNewItems,
    getLastCursor,
    additionalVariables,
    getItemId,
    skip: !postId || !isOpen,
  });

  const uniqueReactions = useMemo(() => {
    const seen = new Set<string>();
    return reactions.filter((r: any) => {
      const key = r?.reaction_id || r?.id;
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [reactions]);

  useEffect(() => {
    if (!hasMore || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "200px" }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [hasMore, loadingMore, loadMore]);

  const content = (
    <div className="p-4">
      <div className="mx-auto bg-inherit border rounded-lg shadow-md p-2 overflow-auto">
        <div className="flex items-center "></div>
        {loading && reactions.length === 0 && <p className="p-4">Loading...</p>}
        {reactions.length === 0 && (
          <p className="p-4 content-center justify-center ">No Reactions Yet</p>
        )}
        {error && <p className="p-4">Error: {error.message}</p>}
        {uniqueReactions.map((reaction: Reaction_INFO) => (
          <ReactionCard key={reaction.reaction_id} reaction={reaction} />
        ))}
        {hasMore && (
          <div ref={loaderRef} className="text-center p-4">
            {loadingMore && <p>Loading more...</p>}
          </div>
        )}
      </div>
    </div>
  );

  // Render desktop dialog and mobile sliding panel similar to SharePanel
  const isDesktop =
    typeof window !== "undefined" ? window.innerWidth >= 768 : true;

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg mx-auto max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Reactions</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 -mr-2">{content}</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Reactions"
      slideFrom="bottom"
      maxHeight="85vh"
      className="max-w-lg mx-auto"
    >
      {content}
    </SlidingPanel>
  );
}
